import { createSlice } from '@reduxjs/toolkit'
import { xor } from 'lodash-es'

import { createSubscriptionEstimate } from './actions/create_subscription_estimate'
import { fetchCheckoutInfo } from './actions/fetch_checkout_info'
import { changeAddonCurrency } from './helpers'

const initialState = {
  loading: true,
  error: null,
  countryPostalCodeError: false,
  additionalQuantity: 0,
  overrideQuantity: 0,
  info: {},
  currencyOptions: [],
  selection: {
    currency: null,
    paymentProvider: 'stripe',
    plan: null,
    quantity: 1,
    billingPeriod: 'year',
    externalCustomerId: null,
    taxExemptionSubmitted: false,
    purchaseSubmitted: false,
    addons: [],
    countryCode: undefined,
    postalCode: undefined
  },
  estimate: null,
  taxlessEstimate: null,
  featureKey: null
}

const checkoutInfoSlice = createSlice({
  name: 'checkoutInfo',
  initialState,
  reducers: {
    resetCheckoutInfo: (state) => ({
      ...initialState,
      currencyOptions: state.currencyOptions,
      selection: {
        ...initialState.selection,
        currency: state.selection.currency
      }
    }),
    setAdditionalQuantity: (state, action) => {
      state.additionalQuantity = action.payload
      state.overrideQuantity = 0
    },
    selectPlan: (state, action) => {
      state.selection.plan = action.payload
    },
    selectQuantity: (state, action) => {
      if (Number.isNaN(action.payload)) {
        state.selection.quantity = ''
      } else {
        state.selection.quantity = action.payload
      }
    },
    selectBillingPeriod: (state, action) => {
      state.selection.billingPeriod = action.payload
    },
    selectPaymentProvider: (state, action) => {
      state.selection.paymentProvider = action.payload
    },
    selectExternalCustomerId: (state, action) => {
      state.selection.externalCustomerId = action.payload
    },
    setSelectedAddonsWithBillingPeriod: (state, action) => {
      const selectedAddons = state.selection.addons
      const newBillingPeriod = action.payload
      const oldBillingPeriod = newBillingPeriod === 'year' ? 'month' : 'year'

      state.selection.addons = selectedAddons.map(
        (skuId) => skuId.replace(oldBillingPeriod, newBillingPeriod)
      )
    },
    setSelectedAddons: (state, action) => {
      state.selection.addons = action.payload
    },
    toggleAddonSelection: (state, action) => {
      state.selection.addons = xor(state.selection.addons, [action.payload.skuId])
    },
    setCheckoutError: (state, action) => {
      state.error = action.payload
    },
    setCountryPostalCodeError: (state, action) => {
      state.countryPostalCodeError = action.payload
    },
    setFeatureKey: (state, action) => {
      state.featureKey = action.payload
    },
    clearCheckoutError: (state) => {
      state.error = null
    },
    startPurchase: (state) => {
      state.selection.purchaseSubmitted = true
    },
    stopPurchase: (state) => {
      state.selection.purchaseSubmitted = false
    },
    selectCurrency: (state, action) => {
      const newCurrency = action.payload.currency
      const audVariant = action.payload.audVariant
      const addons = state.selection.addons

      state.selection.currency = newCurrency
      state.selection.addons = changeAddonCurrency(newCurrency, addons, audVariant)
    },
    loadAvailableCurrencies: (state, action) => {
      const currencies = action.payload.currencies
      state.currencyOptions = currencies
      if (!state.selection.currency || !currencies.includes(state.selection.currency)) {
        state.selection.currency = currencies[0]
      }
    },
    setCountryCode: (state, { payload }) => {
      state.selection.countryCode = payload
    },
    setPostalCode: (state, { payload }) => {
      state.selection.postalCode = payload
    },
    setAddress: (state, { payload }) => {
      state.selection.countryCode = payload.countryCode
      state.selection.postalCode = payload.postalCode
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCheckoutInfo.pending, (state) => {
      state.loading = true
      state.error = null
      state.info = {}
    })
    builder.addCase(fetchCheckoutInfo.fulfilled, (state, action) => {
      state.loading = false
      state.info = action.payload
      state.selection.quantity = action.payload.seat_minimum

      const frequencyOptions = action.payload.frequency_options.map((option) => option.frequency)
      if (!frequencyOptions.includes(state.selection.billingPeriod)) {
        state.selection.billingPeriod = frequencyOptions[0]
      }
    })
    builder.addCase(createSubscriptionEstimate.pending, (state) => {
      state.estimate = null

      const errorType = state.error?.type
      if ((errorType === 'subscriptionEstimate') || (errorType === 'purchase')) {
        state.error = null
      }
    })
    builder.addCase(createSubscriptionEstimate.fulfilled, (state, action) => {
      state.estimate = action.payload[0].resource
      state.taxlessEstimate = action.payload[1]?.resource
    })
    builder.addCase(createSubscriptionEstimate.rejected, (state, action) => {
      state.error = action.payload
    })
  }
})

export const {
  reducer,
  actions: {
    clearCheckoutError,
    loadAvailableCurrencies,
    resetCheckoutInfo,
    selectBillingPeriod,
    selectCurrency,
    selectExternalCustomerId,
    selectPaymentProvider,
    selectPlan,
    selectQuantity,
    setAdditionalQuantity,
    setAddress,
    setCheckoutError,
    setCountryCode,
    setCountryPostalCodeError,
    setFeatureKey,
    setPostalCode,
    setSelectedAddons,
    setSelectedAddonsWithBillingPeriod,
    startPurchase,
    stopPurchase,
    toggleAddonSelection
  }
} = checkoutInfoSlice
